import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import * as React from "react";
import { Button, Table, Modal } from "react-bootstrap";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { withTranslation, WithTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { IApplicationState } from "../store";
import { IFailedUploadQueryResult, IFailedUploadFetchRequest, IFailedUpload } from "../store/failedupload";
import * as failedUploadActions from "../store/failedupload/actions";
import { openIcon } from "../styles/icons";
import LoadingSpinner from "./LoadingSpinner";
import MaxLengthText from "./MaxLengthText";

interface IPropertiesFromState {
  data: IFailedUploadQueryResult;
  loading: boolean;
  errors?: string;
  token?: string;
}

interface IPropertiesFromDispatch {
  fetchFailedUploads: typeof failedUploadActions.fetchRequest;
}

interface IFailedUploadListParameters {
  instituteId: number;
}

interface IFailedUploadListState {
  showFileModal: boolean;
  fileModalContent: string;
  fileModalLoading: boolean;
  fileModalTitle: string;
}

class FailedUploadList extends React.Component<IPropertiesFromState & IPropertiesFromDispatch & IFailedUploadListParameters & WithTranslation, IFailedUploadListState> {

  public readonly state: IFailedUploadListState = {
    showFileModal: false,
    fileModalContent: "",
    fileModalLoading: false,
    fileModalTitle: "",
  };

  public render() {
    const { data, loading, errors, t, instituteId, fetchFailedUploads } = this.props;

    const loadPage = (pageNumber: number) => {
      fetchFailedUploads({instituteId: instituteId, pageSize: 10, page: pageNumber});
    };

    const openFileModal = async (failedUpload: IFailedUpload) => {
      this.setState({ showFileModal: true, fileModalLoading: true, fileModalContent: "", fileModalTitle: failedUpload.fileName });
      try {
        const response = await fetch(`/api/faileduploads/${failedUpload.id}/file`, {
          headers: {
            "Authorization": `Bearer ${this.props.token}`,
          },
        });
        if (response.ok) {
          const content = await response.text();
          this.setState({ fileModalContent: content, fileModalLoading: false });
        } else {
          this.setState({ fileModalContent: t("pages.failedUploads.fileLoadError"), fileModalLoading: false });
        }
      } catch (error) {
        this.setState({ fileModalContent: t("pages.failedUploads.fileLoadError"), fileModalLoading: false });
      }
    }

    const handleCloseFileModal = () => {
      this.setState({ showFileModal: false, fileModalContent: "", fileModalTitle: "" });
    }

    const calculateMessageArrayForPaging = () => {
      const maxPages = 20;
      var startPage = 0;
      var endPage = 0;
      if (data.totalPages <= maxPages) {
        startPage = 1;
        endPage = data.totalPages;
      } else {
        const maxPagesBeforeCurrentPage = Math.floor(maxPages / 2);
        const maxPagesAfterCurrentPage = Math.floor(maxPages / 2) - 1;
        if (data.pageNumber <= maxPagesBeforeCurrentPage) {
          // current page near the start
          startPage = 1;
          endPage = maxPages;
        } else if (data.pageNumber + maxPagesAfterCurrentPage >= data.totalPages) {
          // current page near the end
          startPage = data.totalPages - maxPages + 1;
          endPage = data.totalPages;
        } else {
          // current page is somewhere in the middle
          startPage = data.pageNumber - maxPagesBeforeCurrentPage;
          endPage = data.pageNumber + maxPagesAfterCurrentPage;
        }
      }
      return Array.from(Array((endPage + 1) - startPage).keys()).map(i => startPage + i);
    };

    if (loading) {
      return <LoadingSpinner i18nKey="progress.fetching" />;
    }
    if (errors !== undefined) {
      return <div><b>{t("pages.failedUploads.loadError")}</b></div>
    }
    if (instituteId === -1) {
      return <div>{t("pages.failedUploads.selectOrigin")}</div>
    }
    if (!data || data.empty === true) {
      return <div>{t("pages.failedUploads.noData")}</div>;
    }
    return (
      <div>
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>{t("pages.failedUploads.date")}</th>
              <th>{t("pages.failedUploads.institute")}</th>
              <th>{t("pages.failedUploads.fileName")}</th>
              <th>{t("pages.failedUploads.errorMessage")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data && data.elements.map((failedUpload) => {
              return (
                <tr key={failedUpload.id}>
                  <td>{moment(failedUpload.date).format("DD.MM.YYYY HH:mm:ss")}</td>
                  <td>{failedUpload.instituteName}</td>
                  <td>{failedUpload.fileName}</td>
                  <td title={failedUpload.errorMessage}><MaxLengthText text={failedUpload.errorMessage} maxLength={80} /></td>
                  <td>
                    {failedUpload.fileAvailable &&
                      <Button variant="link" onClick={() => openFileModal(failedUpload)} title={t("pages.failedUploads.showFile")}>
                        <FontAwesomeIcon icon={openIcon} pull="right"/>
                      </Button>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {data && data.totalPages > 1 && <div>
          <Pagination listClassName="rowNoPadding">
              <PaginationItem disabled={data.firstPage}>
                <PaginationLink first onClick={() => loadPage(0)}/>
              </PaginationItem>
              <PaginationItem disabled={data.firstPage}>
                <PaginationLink previous onClick={() => loadPage(data.pageNumber - 1)}/>
              </PaginationItem>
              {
                calculateMessageArrayForPaging().map((index) => {
                  return (
                    <PaginationItem key={`failedUploadList_${index}`} active={index === data.pageNumber + 1}>
                      <PaginationLink onClick={() => loadPage(index - 1)}>{index}</PaginationLink>
                    </PaginationItem>
                  );
                })
              }
              <PaginationItem disabled={data.lastPage}>
                <PaginationLink next onClick={() => loadPage(data.pageNumber + 1)}/>
              </PaginationItem>
              <PaginationItem disabled={data.lastPage}>
                <PaginationLink last onClick={() => loadPage(data.totalPages - 1)}/>
              </PaginationItem>
          </Pagination>
        </div>}
        <Modal show={this.state.showFileModal} onHide={handleCloseFileModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{this.state.fileModalTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.state.fileModalLoading ? (
              <LoadingSpinner i18nKey="progress.fetching" />
            ) : (
              <pre style={{whiteSpace: "pre-wrap", fontSize: "12px", maxHeight: "600px", overflow: "auto"}}>
                {this.state.fileModalContent}
              </pre>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseFileModal}>
              {t("pages.failedUploads.close")}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (store: IApplicationState) => {
  return {
    data: store.failedupload.data,
    loading: store.failedupload.loading,
    errors: store.failedupload.errors,
    token: store.authentication.token,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchFailedUploads: (request: IFailedUploadFetchRequest) => dispatch(failedUploadActions.fetchRequest(request)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(FailedUploadList));
